import React, { useState, useEffect, useRef } from "react";
import "./Hero.css";
import RequestServiceForm from "./RequestServiceForm"; // Import the new component

const images = [
  "/images/01_hero-bg-image.png",
  "/images/02_hero-bg-image.png",
  "/images/03_hero-bg-image.png",
  "/images/04_hero-bg-image.png",
];

const SECTION_HEIGHT = 600; // Increased for slower transition and more scroll space

// Animation constants for Text Line 1 (Discover...)
const TEXT1_ANIMATION_START = SECTION_HEIGHT / 2;
const TEXT1_ANIMATION_END = SECTION_HEIGHT * 1.5;

// Animation constants for Text Line 2 (Twenty Years...)
const TEXT2_ANIMATION_START = TEXT1_ANIMATION_END + SECTION_HEIGHT / 2;
const TEXT2_ANIMATION_END = TEXT2_ANIMATION_START + SECTION_HEIGHT;

// Text1 fades out as Text2 fades in
const TEXT1_FADE_OUT_START = TEXT2_ANIMATION_START;
const TEXT1_FADE_OUT_END = TEXT2_ANIMATION_END;

const BUTTON_ANIMATION_START = TEXT2_ANIMATION_END; // Button appears after Text2 is fully animated
const BUTTON_ANIMATION_END = TEXT2_ANIMATION_END + SECTION_HEIGHT; // Button fully visible after this
// Multiplier to make touch swipes advance progress faster on mobile
const TOUCH_SCROLL_MULTIPLIER = 2.6;
// Separate pacing for image crossfade so images can traverse faster than text
const IMAGE_SECTION_HEIGHT = 180; // smaller => faster crossfade; 3 steps => ~540px total

const Hero = ({ onLearnMoreClick }) => {
  const [progress, setProgress] = useState(0);
  const [showForm, setShowForm] = useState(false); // New state for form visibility
  const heroRef = useRef(null);
  const bookNowButtonAppearedRef = useRef(false); // To track if the button has appeared
  const [animationState, setAnimationState] = useState("idle");
  const [isHeroFullyInView, setIsHeroFullyInView] = useState(false); // New state
  const touchStartYRef = useRef(null);
  const lastTouchYRef = useRef(null);
  const cumulativeTouchDeltaRef = useRef(0);
  const rafIdRef = useRef(null);

  const totalScrollHeight = BUTTON_ANIMATION_END + SECTION_HEIGHT;

  useEffect(() => {
    if (showForm) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [showForm]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Treat the hero as "in view" when it's intersecting at all.
        // Mobile browsers with URL bars or fixed nav rarely reach 1.0 ratio.
        setIsHeroFullyInView(entry.isIntersecting);

        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          setAnimationState("idle");
          // Do not reset progress to 0 here
        } else if (!entry.isIntersecting && entry.boundingClientRect.top > 0) {
          // If hero is not intersecting and is above the viewport, set state to finished
          setAnimationState("finished");
        }
      },
      { threshold: [0, 0.6, 1] } // Observe at 0%, 60%, and 100% visibility
    );

    const heroElement = heroRef.current;
    if (heroElement) {
      observer.observe(heroElement);
    }

    return () => {
      if (heroElement) {
        observer.unobserve(heroElement);
      }
    };
  }, []);

  // Smoothly animate progress to a target for snap-to-milestone behavior
  const animateProgressTo = (target, duration = 550) => {
    const start = performance.now();
    const from = progress;
    const to = Math.max(0, Math.min(target, totalScrollHeight));
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    setAnimationState("animating");

    const step = (now) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(t);
      const value = from + (to - from) * eased;
      setProgress(value);
      if (t < 1) {
        rafIdRef.current = requestAnimationFrame(step);
      } else {
        // settle at exact target
        setProgress(to);
        if (to <= 0) setAnimationState("idle");
        else if (to >= totalScrollHeight) setAnimationState("finished");
        else setAnimationState("idle");
        rafIdRef.current = null;
      }
    };
    rafIdRef.current = requestAnimationFrame(step);
  };

  useEffect(() => {
    const handleWheel = (e) => {
      if (!isHeroFullyInView) {
        return; // Allow normal page scroll if hero is not fully in view
      }

      const maxProgress = totalScrollHeight;
      let newProgress = progress + e.deltaY; // Use current progress, not prevProgress for initial check

      // Determine if we should prevent default scroll
      let shouldPreventDefault = false;

      if (animationState === "animating") {
        shouldPreventDefault = true;
      } else if (animationState === "idle" && e.deltaY > 0 && newProgress > 0) {
        // Scrolling down from idle, and animation is about to start
        shouldPreventDefault = true;
      } else if (
        animationState === "finished" &&
        e.deltaY < 0 &&
        newProgress < maxProgress
      ) {
        // Scrolling up from finished, and animation is about to restart
        shouldPreventDefault = true;
      }

      if (shouldPreventDefault) {
        e.preventDefault();
      } else {
        // If not preventing default, and we are at the boundaries, allow natural scroll
        // This is crucial for scrolling past the hero section
        if (
          (newProgress <= 0 && e.deltaY < 0) ||
          (newProgress >= maxProgress && e.deltaY > 0)
        ) {
          return; // Allow natural scroll if at boundaries and trying to scroll further
        }
      }

      setProgress((prevProgress) => {
        let updatedProgress = prevProgress + e.deltaY;

        if (updatedProgress < 0) {
          setAnimationState("idle");
          return 0;
        } else if (updatedProgress > maxProgress) {
          setAnimationState("finished");
          return maxProgress;
        } else {
          setAnimationState("animating");
          return updatedProgress;
        }
      });
    };

    const heroElement = heroRef.current;
    if (heroElement) {
      heroElement.addEventListener("wheel", handleWheel, { passive: false });
    }

    return () => {
      if (heroElement) {
        heroElement.removeEventListener("wheel", handleWheel);
      }
    };
  }, [totalScrollHeight, animationState, isHeroFullyInView, progress]);

  // Touch support for mobile: translate vertical swipes to the same progress logic
  useEffect(() => {
    const heroElement = heroRef.current;
    if (!heroElement) return;

    const handleTouchStart = (e) => {
      if (e.touches.length !== 1) return;
      touchStartYRef.current = e.touches[0].clientY;
      lastTouchYRef.current = e.touches[0].clientY;
      cumulativeTouchDeltaRef.current = 0;
    };

    const handleTouchMove = (e) => {
      if (e.touches.length !== 1) return;
      if (!isHeroFullyInView) {
        return; // Allow normal page scroll if hero is not fully in view
      }

      const currentY = e.touches[0].clientY;
      const lastY = lastTouchYRef.current ?? currentY;
      const deltaY = lastY - currentY; // positive when swiping up (scroll down)
      lastTouchYRef.current = currentY;

      // Speed up the animation on touch devices by scaling the delta
      const effectiveDelta = deltaY * TOUCH_SCROLL_MULTIPLIER;
      cumulativeTouchDeltaRef.current += effectiveDelta;

      const maxProgress = totalScrollHeight;
      const newProgress = progress + effectiveDelta;

      let shouldPreventDefault = false;

      if (animationState === "animating") {
        shouldPreventDefault = true;
      } else if (animationState === "idle" && effectiveDelta > 0 && newProgress > 0) {
        shouldPreventDefault = true;
      } else if (
        animationState === "finished" &&
        effectiveDelta < 0 &&
        newProgress < maxProgress
      ) {
        shouldPreventDefault = true;
      }

      if (shouldPreventDefault) {
        // Only call preventDefault if allowed (passive: false listener configured)
        if (e.cancelable) e.preventDefault();
      } else {
        if ((newProgress <= 0 && effectiveDelta < 0) || (newProgress >= maxProgress && effectiveDelta > 0)) {
          return; // Allow natural scroll at boundaries
        }
      }

      setProgress((prevProgress) => {
        let updatedProgress = prevProgress + effectiveDelta;
        if (updatedProgress < 0) {
          setAnimationState("idle");
          return 0;
        } else if (updatedProgress > maxProgress) {
          setAnimationState("finished");
          return maxProgress;
        } else {
          setAnimationState("animating");
          return updatedProgress;
        }
      });
    };

    const handleTouchEnd = () => {
      // Snap to milestones on a single swipe for a guided experience
      const swipeDelta = cumulativeTouchDeltaRef.current;
      const threshold = 20; // minimal effective delta to consider it a swipe
      if (Math.abs(swipeDelta) >= threshold) {
        const p = progress;
        if (swipeDelta > 0) {
          // Swipe up: advance to next key milestone
          if (p < TEXT1_ANIMATION_END) {
            animateProgressTo(TEXT1_ANIMATION_END);
          } else if (p < BUTTON_ANIMATION_END) {
            animateProgressTo(BUTTON_ANIMATION_END);
          } else if (p < totalScrollHeight) {
            animateProgressTo(totalScrollHeight);
          }
        } else {
          // Swipe down: go back to previous key milestone
          if (p > BUTTON_ANIMATION_END) {
            animateProgressTo(BUTTON_ANIMATION_END);
          } else if (p > TEXT1_ANIMATION_END) {
            animateProgressTo(TEXT1_ANIMATION_END);
          } else if (p > 0) {
            animateProgressTo(0);
          }
        }
      }

      touchStartYRef.current = null;
      lastTouchYRef.current = null;
      cumulativeTouchDeltaRef.current = 0;
    };

    heroElement.addEventListener("touchstart", handleTouchStart, { passive: true });
    heroElement.addEventListener("touchmove", handleTouchMove, { passive: false });
    heroElement.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      heroElement.removeEventListener("touchstart", handleTouchStart);
      heroElement.removeEventListener("touchmove", handleTouchMove);
      heroElement.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isHeroFullyInView, progress, totalScrollHeight, animationState]);

  // Cleanup any pending animation frame on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (progress > BUTTON_ANIMATION_END && !bookNowButtonAppearedRef.current) {
      bookNowButtonAppearedRef.current = true;
    } else if (
      progress <= BUTTON_ANIMATION_END &&
      bookNowButtonAppearedRef.current
    ) {
      bookNowButtonAppearedRef.current = false;
    }
  }, [progress]);

  useEffect(() => {
    if (animationState === "animating") {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
  }, [animationState]);

  const handleBookNowClick = () => {
    setShowForm(true);
  };

  const handleOverlayClick = () => {
    setShowForm(false);
  };

  const handleFormClick = (e) => {
    e.stopPropagation(); // Prevent clicks on the form from closing the overlay
  };

  // Linear two-image crossfade for a smoother, "dimmer switch" feel
  const opacities = images.map((_, i) => {
    if (IMAGE_SECTION_HEIGHT <= 0) return i === 0 ? 1 : 0;
    const indexFloat = Math.max(0, progress) / IMAGE_SECTION_HEIGHT;
    const base = Math.floor(indexFloat);
    const frac = indexFloat - base;
    const lastIdx = images.length - 1;

    if (base >= lastIdx) {
      // Past the last transition: keep last image at full opacity
      return i === lastIdx ? 1 : 0;
    }

    if (i === base) return 1 - frac; // fade out current
    if (i === base + 1) return frac;  // fade in next
    return 0;
  });

  // Text Line 1 Animation (Discover...)
  let text1Opacity = 0;
  let text1TranslateY = 50;

  if (!showForm) {
    // Only animate if form is not shown
    if (progress >= TEXT1_ANIMATION_START && progress <= TEXT1_ANIMATION_END) {
      const animationProgress =
        (progress - TEXT1_ANIMATION_START) /
        (TEXT1_ANIMATION_END - TEXT1_ANIMATION_START);
      text1Opacity = animationProgress;
      text1TranslateY = 50 * (1 - animationProgress);
    } else if (
      progress > TEXT1_ANIMATION_END &&
      progress < TEXT1_FADE_OUT_START
    ) {
      text1Opacity = 1;
      text1TranslateY = 0;
    } else if (
      progress >= TEXT1_FADE_OUT_START &&
      progress <= TEXT1_FADE_OUT_END
    ) {
      const animationProgress =
        (progress - TEXT1_FADE_OUT_START) /
        (TEXT1_FADE_OUT_END - TEXT1_FADE_OUT_START);
      text1Opacity = 1 - animationProgress;
      text1TranslateY = -50 * animationProgress;
    } else if (progress > TEXT1_FADE_OUT_END) {
      // Ensure it's completely gone after fade out
      text1Opacity = 0;
      text1TranslateY = -50; // Keep it moved up
    }
  } else {
    // Fade out and move up when form is shown
    text1Opacity = 0;
    text1TranslateY = -100; // Move up
  }

  // Text Line 2 Animation (Twenty Years...)
  let text2Opacity = 0;
  let text2TranslateY = 50;

  if (!showForm) {
    // Only animate if form is not shown
    if (progress >= TEXT2_ANIMATION_START && progress <= TEXT2_ANIMATION_END) {
      const animationProgress =
        (progress - TEXT2_ANIMATION_START) /
        (TEXT2_ANIMATION_END - TEXT2_ANIMATION_START);
      text2Opacity = animationProgress;
      text2TranslateY = 50 * (1 - animationProgress);
    } else if (progress > TEXT2_ANIMATION_END) {
      // Remain visible after animation
      text2Opacity = 1;
      text2TranslateY = 0;
    }
  } else {
    // Fade out and move up when form is shown
    text2Opacity = 0;
    text2TranslateY = -100; // Move up
  }

  let buttonOpacity = 0;
  let buttonTranslateY = 50; // Start 50px from bottom

  if (!showForm) {
    // Only animate if form is not shown
    if (
      progress >= BUTTON_ANIMATION_START &&
      progress <= BUTTON_ANIMATION_END
    ) {
      const animationProgress =
        (progress - BUTTON_ANIMATION_START) /
        (BUTTON_ANIMATION_END - BUTTON_ANIMATION_START);
      buttonOpacity = animationProgress;
      buttonTranslateY = 50 * (1 - animationProgress);
    } else if (progress > BUTTON_ANIMATION_END) {
      buttonOpacity = 1;
      buttonTranslateY = 0;
    }
  } else {
    // Fade out and move up when form is shown
    buttonOpacity = 0;
    buttonTranslateY = -100; // Move up
  }

  let dimmerOpacity = 0;
  if (progress >= BUTTON_ANIMATION_START && progress <= BUTTON_ANIMATION_END) {
    const animationProgress =
      (progress - BUTTON_ANIMATION_START) /
      (BUTTON_ANIMATION_END - BUTTON_ANIMATION_START);
    dimmerOpacity = animationProgress * 0.5; // Dims to 50% opacity
  } else if (progress > BUTTON_ANIMATION_END) {
    dimmerOpacity = 0.5;
  }

  let learnMoreOpacity = 0;
  let learnMoreTranslateY = 0;

  if (!showForm) {
    // Only animate if form is not shown
    if (
      progress >= BUTTON_ANIMATION_START &&
      progress <= BUTTON_ANIMATION_END
    ) {
      const animationProgress =
        (progress - BUTTON_ANIMATION_START) /
        (BUTTON_ANIMATION_END - BUTTON_ANIMATION_START);
      learnMoreOpacity = animationProgress;
    } else if (progress > BUTTON_ANIMATION_END) {
      learnMoreOpacity = 1;
    }
  } else {
    // Fade out and move down when form is shown
    learnMoreOpacity = 0;
    learnMoreTranslateY = 100; // Move down
  }

  return (
    <div ref={heroRef} className="hero">
      {images.map((image, index) => (
        <div
          key={index}
          className="hero-bg"
          style={{
            backgroundImage: `url(${image})`,
            opacity: opacities[index],
            zIndex: index, // Reverted zIndex
          }}
        />
      ))}
      <div className="background-dimmer" style={{ opacity: dimmerOpacity }} />
      <div
        className="hero-text-line1"
        style={{
          opacity: text1Opacity,
          transform: `translateX(-50%) translateY(${text1TranslateY}px)`,
        }}
      >
        Discover the Power of Professional Expertise
      </div>
      <div
        className="hero-text-line2"
        style={{
          opacity: text2Opacity,
          transform: `translateX(-50%) translateY(${text2TranslateY}px)`,
        }}
      >
        Twenty Years of Electrical Care in Calgary
      </div>
      <button
        className="book-now-button"
        style={{
          opacity: buttonOpacity,
          transform: `translateX(-50%) translateY(${buttonTranslateY}px)`,
        }}
        onClick={handleBookNowClick} // Add onClick handler
      >
        Book Now
      </button>
      <div
        className="learn-more-container"
        style={{
          opacity: learnMoreOpacity,
          transform: `translateX(-50%) translateY(${learnMoreTranslateY}px)`,
          pointerEvents: "auto",
        }}
        onClick={onLearnMoreClick}
      >
        <p className="learn-more-text">Learn More</p>
        <div className="chevron"></div>
      </div>
      {showForm && (
        <div className="form-overlay" onClick={handleOverlayClick}>
          <RequestServiceForm
            show={showForm}
            onClick={handleFormClick}
            onClose={handleOverlayClick}
          />
        </div>
      )}
    </div>
  );
};

export default Hero;
